import { useState, useEffect, useMemo } from 'react';
import {
  Stack,
  Title,
  Text,
  Badge,
  Group,
  Loader,
  Center,
  Image,
  Card,
  Pagination,
  SimpleGrid,
  Box,
  Alert,
} from '@mantine/core';
import { Camera, Car, AlertCircle, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import type { Detection } from '../types';
import { storage } from '../config/firebase';
import { ref, getDownloadURL } from 'firebase/storage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:9000';

interface PersonaDetectionsSectionProps {
  personaId: string;
  detections: Detection[];
  totalCount: number;
  loading: boolean;
  onRefresh: () => void;
}

export function PersonaDetectionsSection({
  detections: allDetections,
  totalCount,
  loading: externalLoading,
}: PersonaDetectionsSectionProps) {
  const [page, setPage] = useState(1);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const pageSize = 10;

  const detections = useMemo(() => {
    const start = (page - 1) * pageSize;
    return allDetections.slice(start, start + pageSize);
  }, [allDetections, page, pageSize]);

  useEffect(() => {
    if (detections.length > 0) {
      loadAllImages(detections);
    }
  }, [detections]);

  const resolveImageUrl = async (imageUrl: string): Promise<string> => {
    try {
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      } else if (imageUrl.startsWith('/api/')) {
        return `${API_BASE_URL}${imageUrl}`;
      } else {
        const imageRef = ref(storage, imageUrl);
        return await getDownloadURL(imageRef);
      }
    } catch {
      return '';
    }
  };

  const loadAllImages = async (detectionsList: Detection[]) => {
    const urls: Record<string, string> = {};

    for (const detection of detectionsList) {
      if (detection.plate?.image_url) {
        const plateKey = `plate-${detection.observation_id}`;
        urls[plateKey] = await resolveImageUrl(detection.plate.image_url);
      }

      for (const face of detection.faces) {
        const faceKey = `face-${detection.observation_id}-${face.image_id}`;
        urls[faceKey] = await resolveImageUrl(face.image_url);
      }
    }

    setImageUrls(urls);
  };

  const detectionsWithVisuals = detections.filter(
    (d) => d.plate || d.faces.length > 0
  );

  const totalPages = Math.ceil(totalCount / pageSize);

  if (externalLoading) {
    return (
      <Center h={200}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3}>Detections</Title>
        <Badge size="lg" variant="light">
          {totalCount} total
        </Badge>
      </Group>

      {detectionsWithVisuals.length === 0 ? (
        <Alert color="gray" icon={<AlertCircle size={16} />}>
          No detections with faces or license plates found.
        </Alert>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {detectionsWithVisuals.map((detection) => (
            <Card key={detection.observation_id} withBorder padding="sm">
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="xs" fw={500}>
                    {format(new Date(detection.timestamp), 'MMM d, yyyy HH:mm')}
                  </Text>
                  <Badge
                    size="xs"
                    color={
                      detection.processing_state === 'completed'
                        ? 'green'
                        : detection.processing_state === 'failed'
                        ? 'red'
                        : 'yellow'
                    }
                  >
                    {detection.processing_state || 'completed'}
                  </Badge>
                </Group>

                {detection.location && (
                  <Group gap="xs">
                    <MapPin size={12} />
                    <Text size="xs" c="dimmed">
                      {detection.location.lat.toFixed(5)}, {detection.location.lon.toFixed(5)}
                    </Text>
                  </Group>
                )}

                {detection.plate && (
                  <Box>
                    <Group gap="xs" mb="xs">
                      <Car size={14} />
                      <Text size="xs" fw={500}>
                        License Plate
                      </Text>
                    </Group>
                    <Badge size="sm" color="green">
                      {detection.plate.gemini_text || detection.plate.text}
                    </Badge>
                    {imageUrls[`plate-${detection.observation_id}`] && (
                      <Box
                        style={{
                          aspectRatio: '1 / 1',
                          overflow: 'hidden',
                          borderRadius: 4,
                          marginTop: 8,
                        }}
                      >
                        <Image
                          src={imageUrls[`plate-${detection.observation_id}`]}
                          alt="License plate"
                          h="100%"
                          w="100%"
                          fit="cover"
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {detection.faces.length > 0 && (
                  <Box>
                    <Group gap="xs" mb="xs">
                      <Camera size={14} />
                      <Text size="xs" fw={500}>
                        Faces ({detection.faces.length})
                      </Text>
                    </Group>
                    <SimpleGrid cols={3} spacing={4}>
                      {detection.faces.slice(0, 3).map((face) => {
                        const faceKey = `face-${detection.observation_id}-${face.image_id}`;
                        return imageUrls[faceKey] ? (
                          <Box
                            key={face.image_id}
                            style={{
                              aspectRatio: '1 / 1',
                              overflow: 'hidden',
                              borderRadius: 4,
                            }}
                          >
                            <Image
                              src={imageUrls[faceKey]}
                              alt={`Face ${face.image_id}`}
                              h="100%"
                              w="100%"
                              fit="cover"
                            />
                          </Box>
                        ) : null;
                      })}
                    </SimpleGrid>
                  </Box>
                )}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {totalPages > 1 && (
        <Center mt="md">
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Center>
      )}
    </Stack>
  );
}
